// Savings detail editor — inline panel for one savings line item.
//
// Same editor pattern as CostDetailPanel: immutable recurring/one-time badge,
// growth-formula chips, live annualised summary, Save/Delete, Esc-to-close.
// Differences: grouped by savings category (no driver / no unit toggle / no P&L
// badge), an optional `confidence` field, and `savings_effective` is omitted in
// v1.

import { useEffect, useRef, useState } from 'react';
import { Icon } from '../Icon';
import type {
  PortalSavingsCategory,
  PortalSavingsCreateRequest,
  PortalSavingsItem,
  PortalSavingsUpdateRequest,
} from '../../api/types';
import { FORMULA_PRESETS, generateFormula, detectFormulaPreset } from '../../lib/financial/formulas';
import { intervalLabel, periodsPerYear, RECURRING_INTERVALS } from './helpers';

interface SavingsDetailPanelProps {
  /** The category this row is for (chosen on add, fixed on edit). */
  category: PortalSavingsCategory;
  item: PortalSavingsItem | null;
  formatCurrency: (value: number | null | undefined) => string;
  onSaveCreate: (body: PortalSavingsCreateRequest) => Promise<void>;
  onSaveUpdate: (savingsId: string, body: PortalSavingsUpdateRequest) => Promise<void>;
  onDelete: (savingsId: string) => Promise<void>;
  onClose: () => void;
}

const PRESET_KEYS = Object.keys(FORMULA_PRESETS);

export function SavingsDetailPanel({
  category,
  item,
  formatCurrency,
  onSaveCreate,
  onSaveUpdate,
  onDelete,
  onClose,
}: SavingsDetailPanelProps) {
  const isEdit = item != null;
  const [recurring, setRecurring] = useState<boolean>(item?.recurring ?? true);
  const [name, setName] = useState<string>(item?.name ?? '');
  const [amount, setAmount] = useState<string>(
    item?.savings != null ? String(item.savings) : '',
  );
  const [interval, setInterval] = useState<number>(item?.recurring_interval ?? 5);
  const [confidence, setConfidence] = useState<string>(
    item?.confidence != null ? String(item.confidence) : '',
  );
  const [preset, setPreset] = useState<string>(
    detectFormulaPreset(item?.formula).preset ?? 'fixed',
  );
  const [saving, setSaving] = useState(false);
  const firstFieldRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    firstFieldRef.current?.focus();
  }, []);

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
    }
  }

  const amountNum = amount ? Number(amount) : 0;
  const annual = recurring ? amountNum * periodsPerYear(interval) : amountNum;

  async function handleSave() {
    setSaving(true);
    try {
      const formula =
        preset === 'fixed' || preset === 'custom'
          ? null
          : generateFormula(preset, FORMULA_PRESETS[preset]?.defaultRate ?? null);
      const savings = amount ? Number(amount) : null;
      const conf = confidence ? Number(confidence) : null;
      if (isEdit && item) {
        const body: PortalSavingsUpdateRequest = {
          name: name.trim() || null,
          savings,
          recurring_interval: recurring ? interval : null,
          formula,
          confidence: conf,
        };
        await onSaveUpdate(item.id, body);
      } else {
        const body: PortalSavingsCreateRequest = {
          name: name.trim() || null,
          category_id: category.id,
          savings,
          recurring,
          recurring_interval: recurring ? interval : null,
          formula,
          confidence: conf,
        };
        await onSaveCreate(body);
      }
      onClose();
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!item) return;
    setSaving(true);
    try {
      await onDelete(item.id);
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-label={`Edit savings — ${category.name ?? 'category'}`}
      onKeyDown={handleKeyDown}
      className="rounded-[10px] border-[1.5px] border-accent bg-white p-4 shadow-[0_4px_16px_rgba(0,0,0,0.08)]"
    >
      <div className="mb-3 flex items-center justify-between">
        <div className="text-sm font-bold text-ink">{category.name ?? 'Savings'}</div>
        {isEdit ? (
          <span
            className="rounded-full border border-line bg-panel px-2 py-0.5 text-[0.66rem] font-bold uppercase tracking-wide text-muted"
            aria-label={recurring ? 'Recurring savings (fixed)' : 'One-time savings (fixed)'}
          >
            {recurring ? 'Recurring' : 'One-time'}
          </span>
        ) : (
          <div className="flex gap-1" role="group" aria-label="Savings type">
            <button
              onClick={() => setRecurring(false)}
              aria-pressed={!recurring}
              className={`rounded-lg border-[1.5px] px-2 py-0.5 text-[0.66rem] font-bold uppercase tracking-wide ${
                !recurring ? 'border-accent bg-accent-light text-accent' : 'border-line bg-white text-muted'
              }`}
            >
              One-time
            </button>
            <button
              onClick={() => setRecurring(true)}
              aria-pressed={recurring}
              className={`rounded-lg border-[1.5px] px-2 py-0.5 text-[0.66rem] font-bold uppercase tracking-wide ${
                recurring ? 'border-accent bg-accent-light text-accent' : 'border-line bg-white text-muted'
              }`}
            >
              Recurring
            </button>
          </div>
        )}
      </div>

      <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-muted">
        Label (optional)
      </label>
      <input
        ref={firstFieldRef}
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="mb-3 w-full rounded-[9px] border-[1.5px] border-line bg-white px-3 py-2 text-sm text-ink outline-none focus:border-accent"
      />

      <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-muted">
        Amount {recurring ? `(per ${intervalLabel(interval).toLowerCase()})` : ''}
      </label>
      <input
        type="number"
        min="0"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        className="mb-3 w-full rounded-[9px] border-[1.5px] border-line bg-white px-3 py-2 text-sm text-ink outline-none focus:border-accent"
      />

      {recurring && (
        <div className="mb-3">
          <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-muted">
            Interval
          </label>
          <select
            value={interval}
            onChange={(e) => setInterval(Number(e.target.value))}
            className="w-full rounded-[9px] border-[1.5px] border-line bg-white px-3 py-2 text-sm text-ink outline-none focus:border-accent"
          >
            {Object.entries(RECURRING_INTERVALS).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="mb-3">
        <div className="mb-1.5 text-xs font-bold uppercase tracking-wide text-muted">Growth</div>
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Growth formula">
          {PRESET_KEYS.map((key) => {
            const on = preset === key;
            return (
              <button
                key={key}
                onClick={() => setPreset(key)}
                className={`rounded-lg border-[1.5px] px-2.5 py-1 text-[0.72rem] font-semibold ${
                  on
                    ? 'border-accent bg-accent-light text-accent'
                    : 'border-line bg-white text-muted hover:border-accent hover:text-accent'
                }`}
              >
                {FORMULA_PRESETS[key].label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mb-3">
        <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-muted">
          Confidence (optional, %)
        </label>
        <input
          type="number"
          min="0"
          max="100"
          value={confidence}
          onChange={(e) => setConfidence(e.target.value)}
          className="w-full rounded-[9px] border-[1.5px] border-line bg-white px-3 py-2 text-sm text-ink outline-none focus:border-accent"
        />
      </div>

      <div className="mb-3 rounded-[9px] bg-panel px-3 py-2 text-xs text-muted">
        Annualised: <span className="font-bold text-ink">{formatCurrency(annual)}</span>
      </div>

      <div className="flex items-center justify-between">
        {isEdit ? (
          <button
            onClick={handleDelete}
            disabled={saving}
            className="inline-flex items-center gap-1 rounded-[9px] border-[1.5px] border-line bg-white px-3 py-1.5 text-[0.79rem] font-semibold text-danger hover:border-danger disabled:opacity-60"
            aria-label="Delete savings"
          >
            <Icon name="closeXs" size={11} /> Delete
          </button>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <button
            onClick={onClose}
            disabled={saving}
            className="rounded-[9px] border-[1.5px] border-line bg-white px-3 py-1.5 text-[0.79rem] font-semibold text-ink hover:border-accent hover:text-accent disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-[9px] bg-accent px-3 py-1.5 text-[0.79rem] font-semibold text-white hover:bg-accent-hover disabled:opacity-60"
          >
            <Icon name="saveSm" size={12} /> {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
